/**
 * hemisphere.ts — Northern / southern hemisphere handling.
 *
 * What actually differs between the hemispheres, and what does not:
 *
 *   Moon phase          same — a full moon is full everywhere at the same instant
 *   Zodiac sign         same — the moon's position is geocentric
 *   Root/leaf/flower/   same — these follow the zodiac sign, so a root day in
 *     fruit day type      Sussex is a root day in Canterbury, New Zealand
 *   Lunar nodes,        same — astronomical events, not observer-dependent
 *     perigee, apogee
 *
 *   Ascending /         REVERSED — this is about the moon's apparent height in
 *     descending          the local sky. Southern-hemisphere biodynamic
 *                         calendars invert it, as the moon climbs when its
 *                         declination moves south rather than north.
 *   Season              SHIFTED SIX MONTHS — August is late summer in Sussex
 *                         and late winter in Canterbury.
 */

export type Hemisphere = 'northern' | 'southern';

const STORAGE_KEY = 'tgc-hemisphere';

/**
 * IANA time zones that sit south of the equator.
 *
 * Deliberately explicit rather than prefix-matched: 'Pacific/' contains
 * Honolulu and Guam, which are northern, and several African and South
 * American zones straddle the equator. This is only ever a first guess — the
 * reader can always override it, and their choice is what gets stored.
 */
const SOUTHERN_ZONES = new Set([
  // Australia & New Zealand
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Broken_Hill', 'Australia/Darwin',
  'Australia/Eucla', 'Australia/Hobart', 'Australia/Lindeman', 'Australia/Lord_Howe',
  'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
  'Pacific/Auckland', 'Pacific/Chatham', 'Pacific/Norfolk',
  // Pacific islands south of the equator
  'Pacific/Apia', 'Pacific/Easter', 'Pacific/Efate', 'Pacific/Fakaofo', 'Pacific/Fiji',
  'Pacific/Guadalcanal', 'Pacific/Noumea', 'Pacific/Pago_Pago', 'Pacific/Port_Moresby',
  'Pacific/Rarotonga', 'Pacific/Tahiti', 'Pacific/Tongatapu',
  // South America
  'America/Araguaina', 'America/Argentina/Buenos_Aires', 'America/Argentina/Catamarca',
  'America/Argentina/Cordoba', 'America/Argentina/Jujuy', 'America/Argentina/La_Rioja',
  'America/Argentina/Mendoza', 'America/Argentina/Rio_Gallegos', 'America/Argentina/Salta',
  'America/Argentina/San_Juan', 'America/Argentina/San_Luis', 'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia', 'America/Asuncion', 'America/Bahia', 'America/Belem',
  'America/Campo_Grande', 'America/Cuiaba', 'America/Fortaleza', 'America/La_Paz',
  'America/Lima', 'America/Maceio', 'America/Manaus', 'America/Montevideo',
  'America/Noronha', 'America/Porto_Velho', 'America/Punta_Arenas', 'America/Recife',
  'America/Rio_Branco', 'America/Santarem', 'America/Santiago', 'America/Sao_Paulo',
  // Southern Africa & Indian Ocean
  'Africa/Blantyre', 'Africa/Bujumbura', 'Africa/Gaborone', 'Africa/Harare',
  'Africa/Johannesburg', 'Africa/Kinshasa', 'Africa/Lubumbashi', 'Africa/Lusaka',
  'Africa/Luanda', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane', 'Africa/Windhoek',
  'Indian/Antananarivo', 'Indian/Mauritius', 'Indian/Reunion',
  // Southern Asia-Pacific
  'Asia/Dili', 'Asia/Jakarta', 'Asia/Jayapura', 'Asia/Makassar',
  // Antarctic stations
  'Antarctica/Casey', 'Antarctica/Davis', 'Antarctica/Macquarie', 'Antarctica/Mawson',
  'Antarctica/McMurdo', 'Antarctica/Palmer', 'Antarctica/Rothera', 'Antarctica/Syowa',
  'Antarctica/Troll', 'Antarctica/Vostok',
]);

/** Best guess from the browser's time zone. Falls back to northern. */
export function guessHemisphere(): Hemisphere {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return SOUTHERN_ZONES.has(zone) ? 'southern' : 'northern';
  } catch {
    return 'northern';
  }
}

/** The reader's stored choice, or a guess from their time zone. */
export function getHemisphere(): Hemisphere {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'northern' || stored === 'southern') return stored;
  } catch {
    // Private browsing or storage disabled — fall through to the guess.
  }
  return guessHemisphere();
}

export function setHemisphere(hemisphere: Hemisphere) {
  try {
    localStorage.setItem(STORAGE_KEY, hemisphere);
  } catch {
    // Nothing to do — the choice just won't persist between visits.
  }
}

export const otherHemisphere = (h: Hemisphere): Hemisphere =>
  h === 'northern' ? 'southern' : 'northern';

export const HEMISPHERE_LABEL: Record<Hemisphere, string> = {
  northern: 'Northern hemisphere',
  southern: 'Southern hemisphere',
};

// ── Moon calendar URLs ────────────────────────────────────────────────────────
// Each hemisphere gets its own URL so both can be indexed and linked. These
// live here rather than in the page module so that linking to the calendar
// doesn't pull the plant database into the linking page's bundle.
const MONTH_SLUG_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** e.g. (2026, 7) → "august-2026". Month is 0-based, as from Date.getMonth(). */
export const monthSlug = (year: number, month: number) =>
  `${MONTH_SLUG_NAMES[month]}-${year}`;

export const moonPath = (year: number, month: number, hemisphere: Hemisphere) =>
  hemisphere === 'southern'
    ? `/moon-planting/southern/${monthSlug(year, month)}`
    : `/moon-planting/${monthSlug(year, month)}`;

export const moonIndexPath = (hemisphere: Hemisphere) =>
  hemisphere === 'southern' ? '/moon-planting/southern' : '/moon-planting';

/**
 * The northern-hemisphere month whose season matches `month` in this hemisphere.
 *
 * The plant database records sowing and harvest months for a northern grower,
 * so a southern reader looking at August needs February's entries. Months are
 * 1-based, matching PlantingCalendar.
 */
export function seasonalMonth(month: number, hemisphere: Hemisphere): number {
  if (hemisphere === 'northern') return month;
  return ((month + 5) % 12) + 1;
}

/**
 * Whether the moon is climbing in this hemisphere's sky.
 *
 * `isMoonAscending` measures rising declination, which is what a northern
 * observer sees as the moon climbing. South of the equator the effect is
 * inverted.
 */
export function ascendingInHemisphere(risingDeclination: boolean, hemisphere: Hemisphere): boolean {
  return hemisphere === 'northern' ? risingDeclination : !risingDeclination;
}
